import React, { useState, useEffect, useContext, useCallback, Fragment } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";

// Material UI
import {
  Grid,
  Button,
  TextField,
  MenuItem,
  IconButton,
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { withStyles } from "@material-ui/core/styles";

// Context and API
import Context from "context/context";
import emailTemplete from "apis/EmailTemplete/emailTemplete";

// Styles
import { styles } from "./styles";

// Custom styled text field
const CssTextField = withStyles({
  root: {
    "& label.Mui-focused": { color: "#D3D3D3" },
    "& .MuiFormLabel-root": { color: "#707070" },
    "& .MuiInputBase-input": { color: "#707070" },
    "& .MuiInput-underline:before": { borderBottomColor: "#707070!important" },
    "& .MuiInput-underline:after": { borderBottomColor: "#707070!important" },
    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: "#707070" },
      "&:hover fieldset": { borderColor: "#707070" },
      "&.Mui-focused fieldset": { borderColor: "#707070" },
      borderRadius: 20,
      marginTop: 0,
    },
  },
})(TextField);

export default function View() {
  const { state, dispatch } = useContext(Context);
  const { updateEmailTemplete } = emailTemplete();
  const history = useHistory();
  const location = useLocation();
  const classes = styles();

  const [states, setstates] = useState({
    template: "",
    status: "",
    enableEmail: "",
    domainName: "",
    replyTo: "",
    data: {},
    edittedData: {},
    isEdit: false,
    loading: false,
  });

  const {
    template,
    status,
    enableEmail,
    domainName,
    replyTo,
    data,
    edittedData,
    isEdit,
    loading,
  } = states;

  const setStates = useCallback((newState) => {
    setstates((prev) => ({ ...prev, ...newState }));
  }, []);

  useEffect(() => {
    if (location?.state?.data) {
      const dataObj = location.state.data;
      setDataObj(dataObj);
    }
  }, [location]);

  const setDataObj = (dataObj) => {
    setStates({
      data: dataObj,
      template: dataObj?.template,
      status: dataObj?.is_active,
      enableEmail: dataObj?.enable_email,
      domainName: dataObj?.domain_name,
      replyTo: dataObj?.reply_to,
    });
  };

  useEffect(() => {
    dispatch({
      type: "SET_PATHNAME",
      payload: ["HOME", "ADMIN", "EMAIL TEMPLATE", "VIEW"],
    });
  }, [dispatch]);

  const handleUpdate = async () => {
    try {
      if (Object.keys(edittedData).length === 0) {
        toast.error("Please alter at least one field.");
        setStates({ loading: false });
        return;
      }

      const hasEmptyFields = Object.keys(edittedData).some(
        (key) => edittedData[key] === "" || edittedData[key] === null
      );

      if (hasEmptyFields) {
        toast.error("Cannot update with empty field.");
        setStates({ loading: false });
        return;
      }

      const payload = {
        template: data?.template,
        status: status,
        enableEmail: enableEmail,
        domainName: domainName,
        replyTo: replyTo,
      };

      const res = await updateEmailTemplete(payload);

      if (res?.data === 1) {
        toast.success("Template updated successfully!");
        history.goBack();
      } else {
        toast.error("Failed to update template.");
      }

      setStates({ loading: false });
    } catch (error) {
      console.error("Update failed:", error);
      toast.error("Something went wrong while updating.");
      setStates({ loading: false });
    }
  };

  return (
    <Fragment>
      <Grid container spacing={0} style={{ padding: 10 }}>
        <Grid item xs={12}>
          <IconButton onClick={() => history.goBack()}>
            <ArrowBackIcon style={{ color: "#305698" }} />
          </IconButton>
          {!isEdit && (
            <Button
              className={`${classes.createBtn} dash-1-filter-btn`}
              onClick={() => setStates({ isEdit: true })}
            >
              Edit
            </Button>
          )}
        </Grid>
      </Grid>

      <Grid container spacing={4} style={{ padding: 10, fontSize: 16 }}>
        <Grid item xs={8}>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <strong className={classes.label}>Template:</strong>
            </Grid>
            <Grid item xs={8}>
              <span className={classes.label}>{template}</span>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={8}>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <strong className={classes.label}>Status:</strong>
            </Grid>
            <Grid item xs={8}>
              {isEdit ? (
                <CssTextField
                  variant="outlined"
                  select
                  fullWidth
                  value={status}
                  onChange={(e) => {
                    setStates({
                      status: e.target.value,
                      edittedData: {
                        ...edittedData,
                        status: e.target.value,
                      },
                    });
                  }}
                >
                  <MenuItem value={true}>True</MenuItem>
                  <MenuItem value={false}>False</MenuItem>
                </CssTextField>
              ) : (
                <span className={classes.label}>{status?.toString()}</span>
              )}
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={8}>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <strong className={classes.label}>Enable Email:</strong>
            </Grid>
            <Grid item xs={8}>
              {isEdit ? (
                <CssTextField
                  variant="outlined"
                  select
                  fullWidth
                  value={enableEmail}
                  onChange={(e) => {
                    setStates({
                      enableEmail: e.target.value,
                      edittedData: {
                        ...edittedData,
                        enableEmail: e.target.value,
                      },
                    });
                  }}
                >
                  <MenuItem value={true}>True</MenuItem>
                  <MenuItem value={false}>False</MenuItem>
                </CssTextField>
              ) : (
                <span className={classes.label}>{enableEmail?.toString()}</span>
              )}
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={8}>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <strong className={classes.label}>Domain Name</strong>
            </Grid>

            <Grid item xs={8}>
              {isEdit ? (
                <CssTextField
                  variant="outlined"
                  fullWidth
                  inputProps={{
                    maxLength: 250,
                  }}
                  value={domainName}
                  onChange={(e) => {
                    setStates({
                      domainName: e.target.value,
                      edittedData: {
                        ...edittedData,
                        domainName: e.target.value,
                      },
                    });
                  }}
                />
              ) : (
                <span className={classes.label}>
                  {domainName || "-"}
                </span>
              )}
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={8}>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <strong className={classes.label}>Reply-To Email</strong>
            </Grid>

            <Grid item xs={8}>
              {isEdit ? (
                <CssTextField
                  variant="outlined"
                  fullWidth
                  multiline
                  placeholder="Enter multiple emails separated by comma"
                  value={replyTo}
                  onChange={(e) => {
                    setStates({
                      replyTo: e.target.value,
                      edittedData: {
                        ...edittedData,
                        replyTo: e.target.value,
                      },
                    });
                  }}
                />
              ) : (
                <span className={classes.label}>
                  {replyTo || "-"}
                </span>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {isEdit && (
        <Grid container spacing={0} style={{ padding: 10 }}>
          <Grid item xs={12} style={{ textAlign: "right" }}>
            <Button
              className={classes.cancelBtn}
              onClick={() => {
                setDataObj(data);
                setStates({ isEdit: false, edittedData: {} });
              }}
            >
              Cancel
            </Button>
            &nbsp;&nbsp;&nbsp;
            <Button
              className={classes.submitBtn}
              onClick={() => {
                setStates({ loading: true });
                handleUpdate();
              }}
              disabled={loading}
            >
              Update
            </Button>
          </Grid>
        </Grid>
      )}
    </Fragment>
  );
}
